import {
	Component,
	HostListener,
	TemplateRef,
	ViewChild,
	computed,
	inject,
	signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { ShellNavService } from "../shell-nav.service";

export interface Command {
	label: string;
	hint?: string;
	icon: string;
	route: string | unknown[];
	group: string;
}

/**
 * Pure ranking so it can be tested without a component. A prefix match sorts
 * above a match in the middle of a label; everything else keeps its original
 * order. No fuzzy matching — over a handful of organizations it would produce
 * confident-looking nonsense for typos.
 */
export function rankCommands(commands: Command[], term: string): Command[] {
	const needle = term.trim().toLowerCase();
	if (!needle) {
		return [...commands];
	}

	return commands
		.map((command, index) => {
			const haystack = `${command.label} ${command.hint ?? ""}`.toLowerCase();
			return { command, index, position: haystack.indexOf(needle) };
		})
		.filter((entry) => entry.position !== -1)
		.sort((a, b) => a.position - b.position || a.index - b.index)
		.map((entry) => entry.command);
}

/**
 * ⌘K / Ctrl-K palette. Searches only data the shell has already loaded — the
 * organizations fetched for the switcher — plus navigation commands. Nothing
 * extra is read from Firestore, so this needs no indexes and no backend work.
 */
@Component({
	selector: "mch-command-palette",
	standalone: true,
	imports: [
		FormsModule,
		MatButtonModule,
		MatDialogModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatTooltipModule,
	],
	templateUrl: "./command-palette.component.html",
	styleUrl: "./command-palette.component.scss",
})
export class CommandPaletteComponent {
	private readonly dialog = inject(MatDialog);
	private readonly router = inject(Router);
	private readonly nav = inject(ShellNavService);

	@ViewChild("palette") private paletteTemplate!: TemplateRef<unknown>;

	private dialogRef?: MatDialogRef<unknown>;

	protected readonly term = signal("");

	protected readonly commands = computed<Command[]>(() => {
		// An organization missing a name or an id cannot be presented or opened,
		// so it is left out rather than listed as a blank row.
		const organizations = this.nav
			.switchableOrganizations()
			.filter((organization) => !!organization.name && !!organization.id)
			.map((organization) => ({
				label: organization.name as string,
				icon: "business",
				route: ["/organizations", organization.id as string, "dashboard"],
				group: "Organizations",
			}));

		const navigation = this.nav.globalItems().map((item) => ({
			label: item.label,
			hint: "go to",
			icon: item.icon,
			route: item.route,
			group: "Go to",
		}));

		return [...organizations, ...navigation];
	});

	protected readonly results = computed(() => rankCommands(this.commands(), this.term()));

	@HostListener("document:keydown", ["$event"])
	onKeydown(event: KeyboardEvent): void {
		if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
			event.preventDefault();
			this.toggle();
		}
	}

	toggle(): void {
		if (this.dialogRef) {
			this.dialogRef.close();
			return;
		}

		this.term.set("");
		this.dialogRef = this.dialog.open(this.paletteTemplate, {
			panelClass: "mch-command-palette-panel",
			width: "560px",
			autoFocus: "first-tabbable",
		});
		this.dialogRef.afterClosed().subscribe(() => (this.dialogRef = undefined));
	}

	protected run(command: Command): void {
		this.dialogRef?.close();
		void this.router.navigate(Array.isArray(command.route) ? command.route : [command.route]);
	}
}
